import { z } from 'zod';

import {
  allergyResponseSchema,
  immunizationResponseSchema,
  labResultResponseSchema,
  medicationResponseSchema,
  noteResponseSchema,
  patientResponseSchema,
  problemResponseSchema,
  vitalResponseSchema,
  type AllergyResponse,
  type ImmunizationResponse,
  type LabResultResponse,
  type MedicationResponse,
  type NoteResponse,
  type PatientResponse,
  type ProblemResponse,
  type VitalResponse,
} from '@emr/shared';

import { apiRequest } from '../client.ts';

/**
 * The patient portal's records-access surface (`BACKLOG.md` W2 pillar 1, `server/src/patientAuth/
 * chartRoutes.ts`) — every route reads the caller's own chart, no `:patientId` anywhere on the wire
 * (the session already names it), and every list returns the full, unfiltered set: that route
 * file's own header is explicit that no confidentiality-label filtering ever applies to a patient's
 * own read of their own record, so there is nothing here for this client to further narrow either.
 */

const patientEnvelopeSchema = z.strictObject({ patient: patientResponseSchema });
const problemsListResponseSchema = z.strictObject({ problems: z.array(problemResponseSchema) });
const allergiesListResponseSchema = z.strictObject({ allergies: z.array(allergyResponseSchema) });
const medicationsListResponseSchema = z.strictObject({
  medications: z.array(medicationResponseSchema),
});
const immunizationsListResponseSchema = z.strictObject({
  immunizations: z.array(immunizationResponseSchema),
});
const vitalsListResponseSchema = z.strictObject({ vitals: z.array(vitalResponseSchema) });
const labResultsListResponseSchema = z.strictObject({
  labResults: z.array(labResultResponseSchema),
});
const notesListResponseSchema = z.strictObject({ notes: z.array(noteResponseSchema) });

export async function fetchMyChartRecord(signal?: AbortSignal): Promise<PatientResponse> {
  const response = await apiRequest(
    '/api/patient-portal/chart/patient',
    patientEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.patient;
}

export async function listMyProblems(signal?: AbortSignal): Promise<readonly ProblemResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/problems',
    problemsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.problems;
}

export async function listMyAllergies(signal?: AbortSignal): Promise<readonly AllergyResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/allergies',
    allergiesListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.allergies;
}

export async function listMyMedications(
  signal?: AbortSignal,
): Promise<readonly MedicationResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/medications',
    medicationsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.medications;
}

export async function listMyImmunizations(
  signal?: AbortSignal,
): Promise<readonly ImmunizationResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/immunizations',
    immunizationsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.immunizations;
}

export async function listMyVitals(signal?: AbortSignal): Promise<readonly VitalResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/vitals',
    vitalsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.vitals;
}

export async function listMyLabResults(
  signal?: AbortSignal,
): Promise<readonly LabResultResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/labs',
    labResultsListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.labResults;
}

export async function listMySignedNotes(signal?: AbortSignal): Promise<readonly NoteResponse[]> {
  const response = await apiRequest(
    '/api/patient-portal/chart/notes',
    notesListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.notes;
}
